import { useEffect } from "react";
import { invoke } from "@tauri-apps/api/core";
import { create } from "zustand";
import { translate } from "@/i18n";
import { desktopBrowserMessages } from "@/i18n/messages/desktop-browser";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform, isWindowsPlatform } from "@/lib/os-platform";
import {
  BROWSERS,
  isBrowserId,
  isEdgeFamily,
  type BrowserId,
} from "./browser-connection";
import { listenWhileMounted } from "./tauri-listener-cleanup";

/** Native progress of the one running extension setup, told to every webview. */
export const EXTENSION_SETUP_EVENT = "denker://browser-extension-setup";
/** An agent worked in a browser without the extension: offer the setup there. */
export const EXTENSION_OFFER_EVENT = "denker://browser-extension-offer";

const STEPS = [
  "opening",
  "add",
  "open_link",
  "extension_off",
  "connecting",
  "restart",
  "consent",
  "connected",
  "outdated",
  "cancelled",
  "page_unavailable",
  "browser_closed",
  "timed_out",
  "failed",
] as const;
export type ExtensionSetupStep = (typeof STEPS)[number];
const ENDS: readonly ExtensionSetupStep[] = [
  "connected",
  "outdated",
  "cancelled",
  "page_unavailable",
  "browser_closed",
  "timed_out",
  "failed",
];
export const isExtensionSetupEnd = (step: ExtensionSetupStep) =>
  ENDS.includes(step);

export type ExtensionSetupProgress = {
  setupId: string;
  browserId: BrowserId;
  /** The setup is the Denker browser's, which runs `browserId` with
   * Denker's own profile, not the user's browser's. */
  denkerBrowser: boolean;
  step: ExtensionSetupStep;
};

/** The extension connects Chromium browsers on macOS and Windows alike. */
export function browserExtensionApplies(): boolean {
  return isTauri() && (isMacPlatform() || isWindowsPlatform());
}

export function parseExtensionSetupProgress(
  value: unknown,
): ExtensionSetupProgress | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  if (
    typeof raw.setup_id !== "string" ||
    !isBrowserId(raw.browser_id) ||
    !STEPS.includes(raw.step as ExtensionSetupStep)
  )
    return null;
  return {
    setupId: raw.setup_id,
    browserId: raw.browser_id,
    denkerBrowser: raw.denker_browser === true,
    step: raw.step as ExtensionSetupStep,
  };
}

/** Whose setup it is, as its toast's title says: the browser's name, or
 * the Denker browser's. */
export const extensionSetupTitle = (
  progress: Pick<ExtensionSetupProgress, "browserId" | "denkerBrowser">,
): string =>
  progress.denkerBrowser
    ? translate(desktopBrowserMessages, "denkerBrowserName")
    : BROWSERS[progress.browserId].name;

type MessageKey = keyof (typeof desktopBrowserMessages)["en"];

/** Each step's words; a step with a `Denker` variant says "the Denker
 * browser" in its place when the setup is the Denker browser's. */
const STEP_KEYS = {
  opening: "extOpening",
  add: "extAdd",
  open_link: "extOpenLink",
  extension_off: "extTurnOn",
  connecting: "connecting",
  restart: "extRestart",
  consent: "extConsent",
  connected: "extConnected",
  outdated: "extOutdated",
  cancelled: "extStopped",
  page_unavailable: "extPageUnavailable",
  browser_closed: "extBrowserClosed",
  timed_out: "extTimedOut",
  failed: "extFailed",
} as const satisfies Record<ExtensionSetupStep, MessageKey>;

type StepKey = (typeof STEP_KEYS)[ExtensionSetupStep] | "extAddEdge";
type DenkerKey = `${StepKey}Denker` & MessageKey;
const isDenkerKey = (key: string): key is DenkerKey =>
  key in desktopBrowserMessages.en;

/** What the person sees for a step, in Settings and the progress toast - in
 * the Denker browser with `denkerBrowser`. */
export function extensionSetupLabel(
  step: ExtensionSetupStep,
  browserId: BrowserId,
  denkerBrowser = false,
): string {
  // The Denker browser too adds it as Edge does, when Edge runs it.
  const key: StepKey =
    step === "add" && isEdgeFamily(browserId) ? "extAddEdge" : STEP_KEYS[step];
  const denker = `${key}Denker`;
  return translate(
    desktopBrowserMessages,
    denkerBrowser && isDenkerKey(denker) ? denker : key,
    { browser: BROWSERS[browserId].name },
  );
}

/** The latest step of the running setup, or of the one that ended last. */
export const useBrowserExtensionSetup = create<{
  progress: ExtensionSetupProgress | null;
}>(() => ({ progress: null }));

/** Follow the running setup: its step now, asked once - this webview may
 * have started mid-setup - then each step it tells. Returns the cleanup. */
export function followExtensionSetup(
  handle: (progress: ExtensionSetupProgress) => void,
): () => void {
  let active = true;
  const stop = listenWhileMounted<unknown>(
    EXTENSION_SETUP_EVENT,
    ({ payload }) => {
      const progress = parseExtensionSetupProgress(payload);
      if (progress) handle(progress);
    },
  );
  void invoke<unknown>("current_extension_setup")
    .then((current) => {
      const progress = parseExtensionSetupProgress(current);
      if (active && progress) handle(progress);
    })
    .catch(() => {});
  return () => {
    active = false;
    stop();
  };
}

/** Keeps this webview's view of the running setup while mounted. */
export function useBrowserExtensionSetupEvents(): void {
  useEffect(() => {
    if (!browserExtensionApplies()) return;
    return followExtensionSetup((progress) =>
      useBrowserExtensionSetup.setState({ progress }),
    );
  }, []);
}

/** Start the setup in `browserId` - in the Denker browser, which runs it,
 * with `denkerBrowser` - as `setupId`, which a caller keeps to cancel it;
 * resolves with the step it ended on. */
export async function startExtensionSetup(
  browserId: BrowserId,
  denkerBrowser = false,
  setupId: string = crypto.randomUUID(),
): Promise<ExtensionSetupStep> {
  const running = useBrowserExtensionSetup.getState().progress;
  if (running && !isExtensionSetupEnd(running.step))
    throw new Error(translate(desktopBrowserMessages, "anotherSetup"));
  const at = (step: ExtensionSetupStep): ExtensionSetupProgress => ({
    setupId,
    browserId,
    denkerBrowser,
    step,
  });
  useBrowserExtensionSetup.setState({ progress: at("opening") });
  try {
    const end = await invoke<unknown>("start_extension_setup", {
      browserId,
      setupId,
      denkerBrowser,
    });
    if (!STEPS.includes(end as ExtensionSetupStep))
      throw new Error("Invalid browser extension setup result");
    const step = end as ExtensionSetupStep;
    // Its last event says the same; this webview may not have heard it.
    if (useBrowserExtensionSetup.getState().progress?.setupId === setupId)
      useBrowserExtensionSetup.setState({ progress: at(step) });
    return step;
  } catch (error) {
    const { progress } = useBrowserExtensionSetup.getState();
    if (progress?.setupId === setupId)
      useBrowserExtensionSetup.setState({ progress: at("failed") });
    throw error;
  }
}

export async function cancelExtensionSetup(setupId: string): Promise<void> {
  await invoke("cancel_extension_setup", { setupId });
}
